<!--
  A rail that does not exist until it is nearly on screen.

  A genre shelf is nineteen rails. Nineteen eager catalogue calls is 2.5 MB of JSON
  parsed on a phone before anything below the fold has been looked at, and the
  measurement that matters (docs/METADATA.md § D) is that the parse — not the
  transfer — is the cost: six rails are 322 KB on the wire and 1.7 MB of JSON.

  So each rail watches its own placeholder and fetches when it comes within 500px
  of the viewport. It fetches ONCE, keyed on the query, and an empty answer removes
  the rail entirely rather than leaving a titled hole.

  DEPTH IS THE RAIL'S OWN CONTRACT NOW. This component no longer windows or
  caps anything: it hands `Rail` the items it has and a `more()` that turns
  the catalogue one further page at a time (`catalogDeep`, cached-by-page and
  polite by construction) until Cinemeta answers short — the category's honest
  end — and answers 'end'. `Rail` windows the DOM (RING_CAP tiles at any
  depth), grows on approach, and loops the category at exhaustion. A rail
  with a stated `limit` (the Top 10) is exhausted the moment it holds its
  ten, which is how a ranked row loops its exact ranking, numerals and all.
-->
<script lang="ts">
  import type { CatalogItem, MediaKind } from '$types';
  import Poster from './Poster.svelte';
  import Rail from './Rail.svelte';
  import { catalog, catalogDeep, CATALOG_PAGE, CATALOG_PAGE_SHORT, type CatalogId } from './cinemeta';
  import { browseHref } from './nav.svelte';

  interface Props {
    title: string;
    /** A quiet second line under the heading. See `Rail`. */
    note?: string | undefined;
    kind: MediaKind;
    catalog: CatalogId;
    genre?: string | undefined;
    /** "All" link on the heading. Defaults to this kind and genre's own shelf. */
    href?: string | undefined;
    /** Skip the observer and fetch immediately — the first rail on a screen. */
    eager?: boolean;
    /**
     * Number the tiles. The catalogue has no rank field, so the number IS the
     * position in this row's own answer — which is why a ranked row has to be
     * titled by what it actually ranks ("Top 10 this week"), never just "Top".
     */
    ranked?: boolean;
    /**
     * A HARD LENGTH, not a depth cap. Most rails leave it unset and run to
     * the category's true end (Documentary is 2,644 titles deep — measured
     * 2026-08-17 — and the rail's DOM stays capped regardless; see Rail's
     * ring). The Top 10 sets 10: exactly ten, ranked, looping its ten.
     */
    limit?: number;
  }

  let {
    title,
    note,
    kind,
    catalog: catalogId,
    genre,
    href,
    eager = false,
    ranked = false,
    limit = Infinity
  }: Props = $props();

  let mount = $state<HTMLElement | null>(null);
  /** Set once by the observer. `near` is that OR an eager caller's say-so. */
  let spotted = $state(false);
  const near = $derived(eager || spotted);
  let items = $state<CatalogItem[] | null>(null);
  let failed = $state(false);

  const query = $derived(`${kind}/${catalogId}/${genre ?? ''}`);
  const allHref = $derived(href ?? browseHref(kind, genre ?? null));

  $effect(() => {
    if (near || !mount) return;
    const node = mount;
    if (typeof IntersectionObserver === 'undefined') {
      spotted = true;
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          spotted = true;
          observer.disconnect();
        }
      },
      { rootMargin: '500px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  });

  /** The category has answered short: there is no deeper page to turn. */
  let drained = false;

  $effect(() => {
    if (!near) return;
    const key = query;
    const [k, id, g] = key.split('/') as [MediaKind, CatalogId, string];
    const abort = new AbortController();
    failed = false;
    drained = false;

    void catalog({ kind: k, catalog: id, ...(g ? { genre: g } : {}) }, abort.signal)
      .then((found) => {
        if (abort.signal.aborted) return;
        // A first page already short is the whole category — judged by the
        // same 40-row line `catalogDeep` uses, because live full pages are
        // occasionally 48–49 when a row is dropped server-side.
        if (found.length < CATALOG_PAGE_SHORT) drained = true;
        items = Number.isFinite(limit) ? found.slice(0, limit) : found;
      })
      .catch((err: unknown) => {
        if ((err as Error)?.name === 'AbortError') return;
        failed = true;
        items = [];
      });

    return () => abort.abort();
  });

  /**
   * THE RAIL'S `more()`: one page deeper per approach, and only for a rail
   * somebody is actually reading — Rail asks when its scroll nears its end,
   * never on mount, so a reader who flicks past costs the endpoint one
   * request exactly as before. `catalogDeep` re-walks its cached pages (the
   * same six-hour `getJson` path, so the walk is free) and fetches exactly
   * the one new page the deeper `want` demands; an answer short of the ask
   * is Cinemeta saying "that is everything", which is the moment this rail
   * starts LOOPING instead of loading. A thrown fetch propagates: Rail
   * treats it as transient and asks again at the next approach, so a bad
   * moment on a train never fakes a category's end.
   */
  async function more(): Promise<'grew' | 'end'> {
    const list = items ?? [];
    if (list.length >= limit || drained) return 'end';

    const want = Math.min(limit, list.length + CATALOG_PAGE);
    const [k, id, g] = query.split('/') as [MediaKind, CatalogId, string];
    const found = await catalogDeep({ kind: k, catalog: id, ...(g ? { genre: g } : {}) }, want);
    if (found.length < want) drained = true;
    if (found.length > list.length) {
      items = found;
      return 'grew';
    }
    return 'end';
  }

  /*
   * As many card-shaped holes as the row will open with, drawn by the CARD, in
   * a REAL rail with the real heading. The placeholder is therefore the same
   * height and the same shape as the answer, and the only thing that changes
   * when the catalogue replies is the pictures. `loop={false}`: a shimmer
   * must never loop — it is a promise, not a row.
   */
  const ghosts = Array.from({ length: 10 }, (_, i) => i);
</script>

{#if items === null}
  <!-- The placeholder is what the observer watches, so it must occupy real height. -->
  <div bind:this={mount} aria-busy="true">
    <Rail {title} {note} {ranked} href={allHref} loop={false}>
      {#each ghosts as n (n)}
        <Poster ghost />
      {/each}
    </Rail>
  </div>
{:else if items.length > 0}
  {#key query}
    <Rail {title} {note} {ranked} href={allHref} {items} {more}>
      {#snippet tile(item: CatalogItem, ordinal: number)}
        <!-- `tagKind={false}`: this rail's query is kind-scoped by construction,
             so a per-tile "Series" badge would repeat what the whole row is. -->
        <Poster
          {item}
          eager={eager && ordinal <= 4}
          rank={ranked ? ordinal : undefined}
          tagKind={false}
        />
      {/snippet}
    </Rail>
  {/key}
{:else if failed}
  <!-- One rail failing is not a screen failing; say nothing and leave the space. -->
{/if}
